import React from "react";
import { Avatar } from "./AvatarBuilder";

const MOOD_STYLES = {
  positive: {
    card: "bg-brand-yellow border-2 border-black",
    accent: "#FF4500",
    badge: "bg-black text-white",
  },
  warning: {
    card: "bg-brand-orange/90 text-white border-2 border-black",
    accent: "#FACC15",
    badge: "bg-white text-black",
  },
  roast: {
    card: "bg-brand-cyan text-white border-2 border-black",
    accent: "#FFFFFF",
    badge: "bg-white text-black",
  },
};

const MOOD_STYLES_DARK = {
  positive: {
    card: "bg-white text-black",
    accent: "#FF4500",
    badge: "bg-brand-orange text-white",
  },
  warning: {
    card: "bg-brand-orange text-white",
    accent: "#FFFFFF",
    badge: "bg-white text-black",
  },
  roast: {
    card: "bg-brand-cyan text-white",
    accent: "#FFFFFF",
    badge: "bg-black text-white",
  },
};

/** Compact award card — used in teacher report + student report. */
export function AwardCard({ award, compact = false, dark = false }) {
  const styles = dark ? MOOD_STYLES_DARK[award.mood] : MOOD_STYLES[award.mood];
  const winner = award.winner;
  const size = compact ? 48 : 72;
  return (
    <div className={`rounded-2xl p-4 flex items-center gap-4 ${styles.card}`} data-testid={`award-${award.id}`}>
      <div
        style={{ background: `#${winner.avatar_config?.bg || "FACC15"}` }}
        className="rounded-xl overflow-hidden border-2 border-black flex-shrink-0"
      >
        <Avatar config={winner.avatar_config} size={size} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest mb-0.5">
          <span className="text-lg">{award.emoji}</span>
          <span className={`px-2 py-0.5 rounded-full text-[9px] ${styles.badge}`}>{award.title}</span>
        </div>
        <div className={`font-heading font-black ${compact ? "text-lg" : "text-xl"} tracking-tight truncate`}>
          {winner.nickname}
        </div>
        <div className={`text-xs mt-0.5 ${dark ? "opacity-80" : "opacity-70"} leading-snug`}>
          {award.tagline}
        </div>
      </div>
    </div>
  );
}

/** Giant award card for projector ceremony. */
export function AwardBig({ award }) {
  const winner = award.winner;
  const colorMap = {
    positive: "#FACC15",
    warning: "#FF4500",
    roast: "#22D3EE",
  };
  const bgColor = colorMap[award.mood];
  return (
    <div className="flex flex-col items-center gap-8 max-w-2xl mx-auto text-center animate-fade-up">
      <div className="text-[clamp(4rem,14vw,13rem)] leading-none">{award.emoji}</div>

      <div>
        <div className="text-sm font-black uppercase tracking-[0.4em]" style={{ color: bgColor }}>
          {award.mood === "roast" ? "Hlavu vzhůru" : "Ocenění"}
        </div>
        <h1 className="font-heading font-black text-[clamp(3rem,9vw,8rem)] leading-[0.9] tracking-tighter mt-2">
          {award.title}
        </h1>
      </div>

      <div className="flex items-center gap-6">
        <div
          style={{ background: `#${winner.avatar_config?.bg || "FACC15"}` }}
          className="rounded-2xl overflow-hidden border-4 border-white shadow-glow-orange"
        >
          <Avatar config={winner.avatar_config} size={180} />
        </div>
        <div className="text-left">
          <div className="text-sm uppercase tracking-widest text-white/50">Vítěz/ka</div>
          <div className="font-heading font-black text-4xl md:text-6xl tracking-tight leading-none">
            {winner.nickname}
          </div>
        </div>
      </div>

      <div className="text-xl md:text-2xl text-white/80 leading-relaxed max-w-xl">
        „{award.tagline}"
      </div>
    </div>
  );
}
